import React, { createContext, useState, useEffect, useContext } from 'react';

const UserContext = createContext();

// USER BACKEND URL - for token exchange and authentication
const USER_BACKEND_URL = 'https://app.loopai.in';

// FALLBACK API URL - used if api_url not returned from token exchange
const FALLBACK_API_URL = 'https://storyboard-panel-pro.preview.emergentagent.com';

/**
 * SECURE TOKEN-BASED AUTHENTICATION
 * 
 * Flow:
 * 1. Dashboard redirects with auth_token in URL (token is short-lived, single-use)
 * 2. App exchanges token via USER_BACKEND_URL (https://app.loopai.in)
 * 3. Response contains: user info, api_key, api_url, credits
 * 4. Credentials stored in app state and sessionStorage (not URL)
 * 5. Token is cleared from URL immediately
 */

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [apiKey, setApiKey] = useState(null);
  const [apiUrl, setApiUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      // STEP 1: Check for auth_token in URL (from dashboard redirect)
      const params = new URLSearchParams(window.location.search);
      const authToken = params.get('auth_token');
      
      if (authToken) {
        console.log('🔐 Auth token detected, exchanging for credentials...');
        
        // Immediately clear token from URL for security
        params.delete('auth_token');
        const cleanUrl = params.toString() 
          ? `${window.location.pathname}?${params.toString()}`
          : window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
        console.log('🔒 Token cleared from URL');
        
        // STEP 2: Exchange token for actual credentials via USER BACKEND
        const tokenResponse = await fetch(
          `${USER_BACKEND_URL}/api/integration/exchange-token?token=${authToken}`,
          {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
          }
        );
        
        if (tokenResponse.ok) {
          const data = await tokenResponse.json();
          console.log('✅ Token exchanged successfully via', USER_BACKEND_URL);
          console.log('📦 Response data:', { 
            user: data.user, 
            credits: data.credits, 
            api_url: data.api_url,
            has_api_key: !!data.api_key 
          });
          
          // Extract credentials from response
          const { user: userData, api_key, api_url, credits } = data;
          
          // Store credentials securely
          setUser({
            ...userData,
            credits: credits || userData?.credits || 0,
            isAuthenticated: true
          });
          setApiKey(api_key);
          // Use api_url from response, fallback to USER_BACKEND_URL for API calls
          const effectiveApiUrl = api_url || USER_BACKEND_URL;
          setApiUrl(effectiveApiUrl);
          
          // Store in sessionStorage for subsequent API calls
          sessionStorage.setItem('apiKey', api_key);
          sessionStorage.setItem('apiUrl', effectiveApiUrl);
          sessionStorage.setItem('userEmail', userData?.email || '');
          sessionStorage.setItem('userId', userData?.id || '');
          sessionStorage.setItem('userName', userData?.name || '');
          sessionStorage.setItem('userCredits', (credits || userData?.credits || 0).toString());
          
          console.log('💾 Credentials stored. API URL:', effectiveApiUrl);
          
          setLoading(false);
          return;
        } else {
          const errorData = await tokenResponse.json().catch(() => ({}));
          console.error('❌ Token exchange failed:', errorData.detail || tokenResponse.status);
          // Continue to try other auth methods
        }
      }
      
      // STEP 3: Try stored credentials (from previous session)
      const storedApiKey = sessionStorage.getItem('apiKey');
      const storedEmail = sessionStorage.getItem('userEmail');
      
      if (storedApiKey && storedEmail) {
        console.log('🔑 Using stored credentials from previous session');
        
        // Build auth headers
        const headers = { 
          'Content-Type': 'application/json',
          'X-API-Key': storedApiKey,
          'X-User-Email': storedEmail
        };
        
        // ALWAYS use USER_BACKEND_URL for credential verification
        const verifyUrl = USER_BACKEND_URL;
        
        // Verify credentials are still valid by fetching user info
        const userResponse = await fetch(`${verifyUrl}/api/auth/me`, {
          credentials: 'include',
          headers
        });
        
        if (userResponse.ok) {
          const userData = await userResponse.json();
          const userInfo = userData.user || userData;
          console.log('✅ Stored credentials verified:', userInfo.email);
          
          setUser({
            ...userInfo,
            isAuthenticated: true
          });
          setApiKey(storedApiKey);
          setApiUrl(verifyUrl);
          // Also update sessionStorage to correct URL
          sessionStorage.setItem('apiUrl', USER_BACKEND_URL);
          setLoading(false);
          return;
        } else {
          // Stored credentials invalid, clear them
          console.log('⚠️ Stored credentials invalid, clearing...');
          sessionStorage.removeItem('apiKey');
          sessionStorage.removeItem('apiUrl');
          sessionStorage.removeItem('userEmail');
          sessionStorage.removeItem('userCredits');
        }
      }
      
      // STEP 4: Try session cookie authentication (if user logged in via dashboard on same domain)
      console.log('🔍 Trying session cookie authentication...');
      const cookieResponse = await fetch(`${USER_BACKEND_URL}/api/auth/me`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (cookieResponse.ok) {
        const userData = await cookieResponse.json();
        const userInfo = userData.user || userData;
        console.log('✅ Authenticated via session cookie:', userInfo.email);
        
        setUser({
          ...userInfo,
          isAuthenticated: true
        });
        sessionStorage.setItem('userEmail', userInfo.email);
        
        // Fetch API key
        const keyResponse = await fetch(`${USER_BACKEND_URL}/api/integration/key`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (keyResponse.ok) {
          const keyData = await keyResponse.json();
          setApiKey(keyData.api_key);
          setApiUrl(keyData.api_url || USER_BACKEND_URL);
          sessionStorage.setItem('apiKey', keyData.api_key);
          sessionStorage.setItem('apiUrl', keyData.api_url || USER_BACKEND_URL);
        }
        
        setLoading(false);
        return;
      }
      
      // STEP 5: No authentication available - guest mode
      console.log('⚠️ No authentication available - guest mode');
      setUser({
        email: 'guest@loopai.com',
        id: 'guest',
        name: 'Guest User',
        credits: 0,
        isAuthenticated: false
      });
      
    } catch (error) {
      console.error('❌ Authentication error:', error);
      
      // Fall back to stored credentials on network error
      const storedApiKey = sessionStorage.getItem('apiKey');
      const storedEmail = sessionStorage.getItem('userEmail');
      const storedCredits = sessionStorage.getItem('userCredits');
      
      if (storedApiKey && storedEmail) {
        console.log('🔄 Using stored credentials due to network error');
        setApiKey(storedApiKey);
        // ALWAYS use USER_BACKEND_URL
        setApiUrl(USER_BACKEND_URL);
        // Update sessionStorage with correct URL
        sessionStorage.setItem('apiUrl', USER_BACKEND_URL);
        setUser({
          email: storedEmail,
          id: sessionStorage.getItem('userId') || 'stored-user',
          name: sessionStorage.getItem('userName') || storedEmail.split('@')[0],
          credits: storedCredits ? parseInt(storedCredits, 10) : 0,
          isAuthenticated: true
        });
      } else {
        setUser({
          email: 'guest@loopai.com',
          id: 'guest',
          name: 'Guest User',
          credits: 0,
          isAuthenticated: false
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshCredits = async () => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      const storedApiKey = sessionStorage.getItem('apiKey');
      const storedEmail = sessionStorage.getItem('userEmail');
      
      if (storedApiKey) headers['X-API-Key'] = storedApiKey;
      if (storedEmail) headers['X-User-Email'] = storedEmail;
      
      // ALWAYS use USER_BACKEND_URL for credit operations
      const creditsUrl = USER_BACKEND_URL;
      
      const response = await fetch(`${creditsUrl}/api/credits/balance`, {
        credentials: 'include',
        headers
      });
      
      if (response.ok) {
        const data = await response.json();
        setUser(prev => ({ ...prev, credits: data.credits }));
        sessionStorage.setItem('userCredits', data.credits.toString());
      }
    } catch (error) {
      console.error('Error refreshing credits:', error);
    }
  };

  const updateCredits = (newCredits) => {
    setUser(prev => ({
      ...prev,
      credits: newCredits
    }));
    sessionStorage.setItem('userCredits', newCredits.toString());
  };
  
  // Logout function - clears all stored credentials
  const logout = () => {
    sessionStorage.removeItem('apiKey');
    sessionStorage.removeItem('apiUrl');
    sessionStorage.removeItem('userEmail');
    sessionStorage.removeItem('userCredits');
    setUser(null);
    setApiKey(null);
    setApiUrl(null);
    console.log('🔓 User logged out, credentials cleared');
  };

  const value = {
    user,
    apiKey,
    apiUrl,
    loading,
    refreshCredits,
    updateCredits,
    logout,
    isAuthenticated: user?.isAuthenticated || false
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) throw new Error('useUser must be used within UserProvider');
  return context;
};
